import type { ArticleCritiqueReport } from './article-critique';
import type { FactCheckClaim, StoredFactCheck } from './article-factcheck';

/**
 * TWO JUDGES OF ONE SENTENCE — the editorial critique and the claim check — must not leave the operator
 * deciding what one of them has already settled.
 *
 * The critique reads retained pages and can say "this legal assertion needs evidence". The claim check reads
 * the cited provision and, for a `supported` verdict, holds an exact quote of the text that states it. When
 * the claim check has verified a sentence against the law and the critique still asks for evidence for the
 * same sentence, the critique's finding is answered. It is not left as an open editorial decision: it is
 * resolved here with a visible note ("Confirmed by the claim check: <quote>"), derived on read from the two
 * stored records so it needs no write, cannot go stale (a check of an earlier revision confirms nothing), and
 * leaves the human trail intact (the finding is still listed, with the quote that answers it).
 *
 * A finding the claim check cannot answer stays open: a `source_conflict` (the critique says a source
 * contradicts the article), a passage with no verified claim behind it, and a passage that spans a claim the
 * check could not verify or found wrong.
 *
 * NOTHING MAY FALSELY PASS (2026-10-05, v4 run). A verified quote answers one question: "is this sentence
 * stated by the source?" It answers a finding only when that was the finding's whole question. So a finding is
 * confirmed only when ALL of these hold:
 *   - it is a plain `unsupported_claim` complaint (an `editorial_judgment` is a person's call, never settled by
 *     a quote);
 *   - it is not an OMISSION finding ("omits X", "leaves out", "does not mention"): a quote that shows the statute
 *     contains X SUPPORTS the critic, it does not answer it (topic c80ffb3b: the finding said the article omits
 *     "before the date of the hearing", and the quote that contained that phrase "confirmed" it);
 *   - the finding's quoted passage lies wholly inside ONE supported claim sentence, so the claim covers every
 *     factual element the finding questions. A passage that is longer than the claim (it spans several facts,
 *     topic 6eb54f2d) leaves the elements outside the claim unchecked, so it stays open.
 */

export type CritiqueFinding = ArticleCritiqueReport['findings'][number];

export interface ClaimCheckConfirmation {
  /** The critique finding this answers. */
  findingKey: string;
  claimId: string;
  /** The exact passage of the fetched law the claim check's verdict rests on. */
  quote: string;
  citation: string | null;
  sourceUrl: string | null;
  /** The sentence the note says is confirmed, as the claim check extracted it. */
  sentence: string;
}

/** A human decision on a finding is keyed by its dimension, quote and suggestion, so it survives a re-render. */
export function articleCritiqueFindingKey(
  finding: Pick<CritiqueFinding, 'dimension' | 'quote' | 'suggestion'>,
): string {
  return [finding.dimension, finding.quote, finding.suggestion].join('\u001f');
}

const MIN_OVERLAP_CHARS = 20;
/** A passage shorter than this many words is too little to say which claim it is about. */
const MIN_COVERED_WORDS = 4;

/** The words of a passage with the markup and the typography taken out, so two copies of one sentence compare equal. */
function plain(text: string): string {
  return text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+/gm, '')
    .replace(/[`*_~|>#]+/g, ' ')
    .replace(/[\u2026]|\.{3}/g, ' ')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** The words of an already-plain passage; a number keeps its point and its sign, nothing else is kept. */
function wordsOf(text: string): string[] {
  return text
    .replace(/[^a-z0-9$%.]+/g, ' ')
    .replace(/(?<![0-9])\.|\.(?![0-9])/g, ' ')
    .split(' ')
    .filter(Boolean);
}

/** Whether `needle` occurs in `haystack` as one unbroken run of words. */
function containsRun(haystack: readonly string[], needle: readonly string[]): boolean {
  if (!needle.length || needle.length > haystack.length) return false;
  for (let at = 0; at + needle.length <= haystack.length; at += 1) {
    let all = true;
    for (let offset = 0; offset < needle.length; offset += 1)
      if (haystack[at + offset] !== needle[offset]) {
        all = false;
        break;
      }
    if (all) return true;
  }
  return false;
}

/**
 * A bold label the article opens a paragraph with ("**Job records.** The Comptroller page says ..."), which
 * the critique's quote sometimes carries and the claim's sentence does not. It states nothing to check.
 */
function withoutLeadLabel(raw: string): string {
  return raw
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+/, '')
    .replace(/^\s*\*\*[^*\n]{1,80}\*\*[.:]?\s*/, '')
    .replace(/^\s*[^*\n.]{1,80}\.\*\*\s*/, '');
}

/**
 * The finding's passage lies wholly inside the claim's sentence, so the claim speaks to every word of it
 * (markup, typography and table-cell separators aside). Containment the other way (the passage is longer than
 * the claim) is NOT coverage: the passage then questions facts the claim never judged.
 */
function covers(findingPlain: string, claimPlain: string): boolean {
  if (findingPlain.length < MIN_OVERLAP_CHARS || !claimPlain) return false;
  const needle = wordsOf(findingPlain);
  if (needle.length < MIN_COVERED_WORDS) return false;
  return containsRun(wordsOf(claimPlain), needle);
}

/** A verdict the claim check can stand behind: supported, with a verified quote, and not in dispute. */
export function isVerifiedSupport(claim: FactCheckClaim): boolean {
  return (
    claim.verdict === 'supported' &&
    claim.quote.trim().length >= 12 &&
    !claim.disputed &&
    claim.applies !== 'misapplied' &&
    claim.applies !== 'unclear'
  );
}

/** Findings that ask for evidence, which is what a verified claim answers. Only a plain complaint that a claim is unsupported. */
function asksForEvidence(finding: CritiqueFinding): boolean {
  return finding.basis === 'unsupported_claim';
}

/** A sentence of a reason that speaks of the SOURCE text (a gap in what was supplied), not of the article. */
const ABOUT_THE_SOURCE =
  /\b(?:provided|supplied|excerpts?|retrieved|truncated|source text|source material|the (?:source|page|guide|resolution|statute|section)\b)/i;
/** Wording that says the article leaves something out. */
const OMISSION_WORDING =
  /\b(?:omit(?:s|ted|ting)?|leaves?\s+(?:out|off)|left\s+(?:out|off)|fails?\s+to\s+(?:mention|include|note|state|address|disclose|reflect|say|cover|explain|specify)|(?:does\s+not|doesn['\u2019]t|never)\s+(?:mention|include|address|reflect|disclose|cover|explain|note|specify|say)|no\s+mention\s+of|(?:is|are)\s+missing|missing\s+(?:the|a|an|any|from)|overlooks?|neglects?|incomplete|without\s+(?:mentioning|noting|saying|stating))\b/i;
/** A remedy that adds content the article lacks. */
const ADDS_CONTENT =
  /^\s*(?:insert|add|include|mention|note|expand|specify|clarify|append|state|spell out|disclose|name)\b/i;

/**
 * A finding that says the article LEAVES SOMETHING OUT, or whose remedy is to ADD something. A quote showing
 * that the statute contains the missing thing supports the critic; it never answers the finding. Gaps in the
 * SOURCE text the critique was shown ("the excerpt does not include ...") are not omissions of the article.
 */
export function isOmissionFinding(
  finding: Pick<CritiqueFinding, 'reason' | 'suggestion'>,
): boolean {
  if (ADDS_CONTENT.test(finding.suggestion)) return true;
  return finding.reason
    .split(/(?<=[.;!?])\s+/)
    .some((sentence) => OMISSION_WORDING.test(sentence) && !ABOUT_THE_SOURCE.test(sentence));
}

/**
 * The critique findings a CURRENT claim check has already answered, by finding key. Empty when there is no
 * check, or it describes an earlier revision.
 */
export function confirmCritiqueFindings(
  findings: readonly CritiqueFinding[],
  factCheck: StoredFactCheck | null | undefined,
): Map<string, ClaimCheckConfirmation> {
  const out = new Map<string, ClaimCheckConfirmation>();
  if (!factCheck?.isCurrent || !findings.length) return out;
  const claims = factCheck.report.claims
    .filter((claim) => claim.kind !== 'event' && claim.verdict !== 'not_applicable')
    .map((claim) => ({ claim, text: plain(claim.text) }));
  for (const finding of findings) {
    if (!asksForEvidence(finding) || isOmissionFinding(finding)) continue;
    const quote = plain(withoutLeadLabel(finding.quote));
    const touched = claims.filter(({ text }) => covers(quote, text));
    if (!touched.length || !touched.every(({ claim }) => isVerifiedSupport(claim))) continue;
    const first = touched[0]!.claim;
    out.set(articleCritiqueFindingKey(finding), {
      findingKey: articleCritiqueFindingKey(finding),
      claimId: first.id,
      quote: first.quote.replace(/\s+/g, ' ').trim(),
      citation: first.citation,
      sourceUrl: first.sourceUrl,
      sentence: first.text,
    });
  }
  return out;
}

/** The note shown beside a finding the claim check answered. */
export function confirmationNote(confirmation: ClaimCheckConfirmation, maxChars = 240): string {
  const quote =
    confirmation.quote.length > maxChars
      ? `${confirmation.quote.slice(0, maxChars - 3).replace(/\s+\S*$/, '')}...`
      : confirmation.quote;
  return `Confirmed by the claim check: "${quote}"${confirmation.citation ? ` (${confirmation.citation})` : ''}`;
}

/**
 * The keys a finding counts as decided under: the operator's own decisions, the ones the claim check answered,
 * and the reviewer's own limits (`reviewerLimitFindingKeys`: the critique could not see the source text, which
 * is a limitation of the review and not an open decision about the article).
 */
export function decidedFindingKeys(
  reviewed: ReadonlySet<string>,
  confirmations: ReadonlyMap<string, ClaimCheckConfirmation> | undefined,
  reviewerLimits?: ReadonlySet<string>,
): ReadonlySet<string> {
  if (!confirmations?.size && !reviewerLimits?.size) return reviewed;
  return new Set([...reviewed, ...(confirmations?.keys() ?? []), ...(reviewerLimits ?? [])]);
}
